import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, HostListener } from '@angular/core';

import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { Cancel01Icon, Search01Icon, Tick01Icon } from '@hugeicons/core-free-icons';

export interface SelectOption {
    label: string;
    value: any;
    subLabel?: string;
}

@Component({
    selector: 'app-select',
    standalone: true,
    imports: [FormsModule, HugeiconsIconComponent],
    templateUrl: './select.component.html',
    styleUrl: './select.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        }
    ]
})
export class SelectComponent implements ControlValueAccessor {
    @Input() options: SelectOption[] = [];
    @Input() placeholder: string = 'Select option';
    @Input() searchable: boolean = true;

    // Icons
    closeIcon = Cancel01Icon;
    searchIcon = Search01Icon;
    checkIcon = Tick01Icon;

    isOpen = false;
    searchValue = '';
    selectedValue: any = null;
    selectedLabel = '';
    dropdownPosition: 'bottom' | 'top' = 'bottom';

    onChange: any = () => { };
    onTouched: any = () => { };

    constructor(private elementRef: ElementRef) { }

    get filteredOptions() {
        if (!this.searchValue) return this.options;
        return this.options.filter(opt =>
            opt.label.toLowerCase().includes(this.searchValue.toLowerCase()) ||
            (opt.subLabel && opt.subLabel.toLowerCase().includes(this.searchValue.toLowerCase()))
        );
    }

    toggle() {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            this.calculateDropdownPosition();
            if (this.searchable) {
                setTimeout(() => {
                    const input = this.elementRef.nativeElement.querySelector('.search-input');
                    if (input) input.focus();
                }, 0);
            }
        }
    }

    calculateDropdownPosition() {
        setTimeout(() => {
            const container = this.elementRef.nativeElement.querySelector('.select-container');
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const dropdownHeight = 300; // max-height from CSS
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // If not enough space below but more space above, open upwards
            if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                this.dropdownPosition = 'top';
            } else {
                this.dropdownPosition = 'bottom';
            }
        }, 0);
    }

    selectOption(option: SelectOption) {
        this.selectedValue = option.value;
        this.selectedLabel = option.label;
        this.onChange(this.selectedValue);
        this.isOpen = false;
        this.searchValue = '';
    }

    clearSelection(event: MouseEvent) {
        event.stopPropagation();
        this.selectedValue = null;
        this.selectedLabel = '';
        this.onChange(null);
    }

    // ControlValueAccessor methods
    writeValue(value: any): void {
        this.selectedValue = value;
        const option = this.options.find(opt => opt.value === value);
        this.selectedLabel = option ? option.label : '';
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
        }
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentMouseDown(event: MouseEvent) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
        }
    }
}
